import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button, Input, Label } from '@jira-clone/ui'
import { Upload, X, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { getApiErrorMessage } from '../../shared/utils/apiError'
import { useCompanyProfile } from '../../features/recruitment/api'

interface AddressForm {
  line1: string
  line2: string
  city: string
  state: string
  country: string
  pincode: string
}

interface CompanyForm {
  name: string
  legalName: string
  logoUrl: string
  signatureUrl: string
  registeredAddress: AddressForm
  gstin: string
  pan: string
  cin: string
  industry: string
  officialEmail: string
  phone: string
  website: string
  timezone: string
  defaultCurrency: string
  fiscalYearStartMonth: number
  workingHours: {
    start: string
    end: string
  }
  // The standard week timesheets measure expected hours against (0 = Sunday ... 6 = Saturday).
  workWeek: {
    workDays: number[]
    hoursPerDay: number
  }
  // Finance Master Data
  bankAccName: string
  bankAccNo: string
  bankName: string
  bankBranch: string
  bankIfsc: string
  bankUpi: string
}

const EMPTY_ADDRESS: AddressForm = { line1: '', line2: '', city: '', state: '', country: '', pincode: '' }

const EMPTY_FORM: CompanyForm = {
  name: '',
  legalName: '',
  logoUrl: '',
  signatureUrl: '',
  registeredAddress: EMPTY_ADDRESS,
  gstin: '',
  pan: '',
  cin: '',
  industry: '',
  officialEmail: '',
  phone: '',
  website: '',
  timezone: 'Asia/Kolkata',
  defaultCurrency: 'INR',
  fiscalYearStartMonth: 4,
  workingHours: {
    start: '09:00',
    end: '18:00',
  },
  workWeek: { workDays: [1, 2, 3, 4, 5], hoursPerDay: 8 },
  // Finance Master Data
  bankAccName: '',
  bankAccNo: '',
  bankName: '',
  bankBranch: '',
  bankIfsc: '',
  bankUpi: '',
}

function Section({ title, children, description }: { title: string; children: React.ReactNode; description?: string }) {
  return (
    <div className="space-y-3 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
        {description && <p className="text-xs text-muted-foreground whitespace-nowrap">{description}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  )
}

function Field({ label, id, value, onChange, placeholder, type = 'text' }: {
  label: string
  id: string
  value: string | number
  onChange: (v: string) => void
  placeholder?: string
  type?: string
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  )
}

function AssetUploadSection({
  title,
  description,
  currentUrl,
  onUpload,
  onRemove,
  uploading,
  removing,
  uploadLabel,
  removeLabel,
}: {
  title: string
  description: string
  currentUrl: string
  onUpload: (file: File) => Promise<void>
  onRemove: () => Promise<void>
  uploading: boolean
  removing: boolean
  uploadLabel: string
  removeLabel: string
}) {
  const [preview, setPreview] = useState<string | null>(currentUrl || null)

  useEffect(() => {
    setPreview(currentUrl || null)
  }, [currentUrl])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only PNG, JPG, and WebP are allowed.')
      return
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      toast.error('File size exceeds 5MB limit.')
      return
    }
    
    // Create preview
    const previewUrl = URL.createObjectURL(file)
    setPreview(previewUrl)
    
    try {
      await onUpload(file)
      toast.success(`${title} uploaded successfully.`)
    } catch {
      setPreview(currentUrl || null)
    } finally {
      e.target.value = ''
    }
  }

  const handleRemove = async () => {
    try {
      await onRemove()
      setPreview(null)
      toast.success(`${title} removed.`)
    } catch {
      // Error already shown by onRemove
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</Label>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        {/* Preview */}
        <div className="relative w-24 h-24 bg-muted/30 rounded-lg border border-border flex items-center justify-center overflow-hidden shrink-0">
          {preview ? (
            <img src={preview} alt={`${title} preview`} className="w-full h-full object-contain p-2" />
          ) : (
            <div className="flex flex-col items-center justify-center gap-1 text-muted-foreground/50">
              <ImageIcon className="w-6 h-6" />
              <span className="text-xs">No {title.toLowerCase()}</span>
            </div>
          )}
        </div>
        
        {/* Upload/Replace button */}
        <div className="flex flex-col gap-2 flex-1 min-w-[200px]">
          <label className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg bg-background hover:bg-muted/50 text-sm font-medium cursor-pointer transition-colors">
            <Upload className="w-4 h-4" />
            <span>{currentUrl || preview ? 'Replace' : uploadLabel}</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleFileSelect}
              className="sr-only"
              disabled={uploading || removing}
            />
          </label>
          <p className="text-xs text-muted-foreground">PNG or JPG, max 2MB</p>
        </div>
        
        {/* Remove button */}
        {(currentUrl || preview) && (
          <button
            onClick={handleRemove}
            disabled={uploading || removing}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>{removeLabel}</span>
            {removing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          </button>
        )}
      </div>
    </div>
  )
}

export default function CompanyProfilePage() {
  const { organization } = useAuth()
  const { data: companyProfile, isLoading, refetch } = useCompanyProfile()
  const [form, setForm] = useState<CompanyForm>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [removingLogo, setRemovingLogo] = useState(false)
  const [uploadingSignature, setUploadingSignature] = useState(false)
  const [removingSignature, setRemovingSignature] = useState(false)

  // Sync form with company profile data
  useEffect(() => {
    if (companyProfile) {
      setForm({
        name: companyProfile.name || '',
        legalName: companyProfile.legalName || '',
        logoUrl: companyProfile.logoUrl || '',
        signatureUrl: companyProfile.signatureUrl || '',
        registeredAddress: { ...EMPTY_ADDRESS, ...(companyProfile.registeredAddress || {}) },
        gstin: companyProfile.gstin || '',
        pan: companyProfile.pan || '',
        cin: companyProfile.cin || '',
        industry: companyProfile.industry || '',
        officialEmail: companyProfile.officialEmail || '',
        phone: companyProfile.phone || '',
        website: companyProfile.website || '',
        timezone: companyProfile.timezone || 'Asia/Kolkata',
        defaultCurrency: companyProfile.defaultCurrency || 'INR',
        fiscalYearStartMonth: companyProfile.fiscalYearStartMonth ?? 4,
        workingHours: {
          start: companyProfile.workingHours?.start || '09:00',
          end: companyProfile.workingHours?.end || '18:00',
        },
        workWeek: {
          workDays: companyProfile.workWeek?.workDays?.length ? companyProfile.workWeek.workDays : [1, 2, 3, 4, 5],
          hoursPerDay: companyProfile.workWeek?.hoursPerDay || 8,
        },
        // Finance Master Data
        bankAccName: companyProfile.bankAccName || '',
        bankAccNo: companyProfile.bankAccNo || '',
        bankName: companyProfile.bankName || '',
        bankBranch: companyProfile.bankBranch || '',
        bankIfsc: companyProfile.bankIfsc || '',
        bankUpi: companyProfile.bankUpi || '',
      })
    }
  }, [companyProfile])

  const setAddress = (key: keyof AddressForm, value: string) => {
    setForm((f) => ({ ...f, registeredAddress: { ...f.registeredAddress, [key]: value } }))
  }

  const handleSave = async () => {
    if (!organization?.id) return
    setSaving(true)
    try {
      await api.patch(
        `/organizations/${organization.id}`,
        {
          name: form.name || undefined,
          legalName: form.legalName || undefined,
          logoUrl: form.logoUrl !== undefined ? (form.logoUrl || null) : undefined,
          signatureUrl: form.signatureUrl !== undefined ? (form.signatureUrl || null) : undefined,
          registeredAddress: form.registeredAddress,
          gstin: form.gstin || undefined,
          pan: form.pan || undefined,
          cin: form.cin || undefined,
          industry: form.industry || undefined,
          officialEmail: form.officialEmail || undefined,
          phone: form.phone || undefined,
          website: form.website || undefined,
          timezone: form.timezone || undefined,
          defaultCurrency: form.defaultCurrency || undefined,
          fiscalYearStartMonth: form.fiscalYearStartMonth,
          workingHours: form.workingHours,
          workWeek: form.workWeek,
          // Finance Master Data
          bankAccName: form.bankAccName || undefined,
          bankAccNo: form.bankAccNo || undefined,
          bankName: form.bankName || undefined,
          bankBranch: form.bankBranch || undefined,
          bankIfsc: form.bankIfsc || undefined,
          bankUpi: form.bankUpi || undefined,
        },
        { skipErrorToast: true }
      )
      toast.success('Company profile saved.')
      // Refetch to get latest data from server
      refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'save the company profile'))
    } finally {
      setSaving(false)
    }
  }

  const uploadLogo = async (file: File) => {
    if (!organization?.id) return
    setUploadingLogo(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      // Don't set Content-Type header - let axios set it with the correct boundary
      const res: any = await api.post(`/organizations/${organization.id}/upload-logo`, formData)
      // Handle both direct response and wrapped response
      const url = res?.url || res?.data?.url || res?.data?.data?.url
      if (!url) {
        throw new Error('No URL returned from server')
      }
      setForm(f => ({ ...f, logoUrl: url }))
      refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'upload logo'))
      throw err
    } finally {
      setUploadingLogo(false)
    }
  }

  const removeLogo = async () => {
    if (!organization?.id) return
    setRemovingLogo(true)
    try {
      await api.delete(`/organizations/${organization.id}/logo`)
      setForm(f => ({ ...f, logoUrl: '' }))
      refetch()
    } finally {
      setRemovingLogo(false)
    }
  }

  const uploadSignature = async (file: File) => {
    if (!organization?.id) return
    setUploadingSignature(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      // Don't set Content-Type header - let axios set it with the correct boundary
      const res: any = await api.post(`/organizations/${organization.id}/upload-signature`, formData)
      // Handle both direct response and wrapped response
      const url = res?.url || res?.data?.url || res?.data?.data?.url
      if (!url) {
        throw new Error('No URL returned from server')
      }
      setForm(f => ({ ...f, signatureUrl: url }))
      refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'upload signature'))
      throw err
    } finally {
      setUploadingSignature(false)
    }
  }

  const removeSignature = async () => {
    if (!organization?.id) return
    setRemovingSignature(true)
    try {
      await api.delete(`/organizations/${organization.id}/signature`)
      setForm(f => ({ ...f, signatureUrl: '' }))
      refetch()
    } finally {
      setRemovingSignature(false)
    }
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading company profile...</p>
  }

  return (
    <div className="space-y-6 max-w-2xl pb-8">
      <div>
        <h2 className="text-lg font-bold text-foreground">Company Profile</h2>
        <p className="text-sm text-muted-foreground">Your organization's identity, registration, and regional settings.</p>
      </div>

      <Section title="Identity">
        <Field label="Display Name" id="cp-name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
        <Field label="Legal Name" id="cp-legal-name" value={form.legalName} onChange={(v) => setForm((f) => ({ ...f, legalName: v }))} />
        <Field label="Industry" id="cp-industry" value={form.industry} onChange={(v) => setForm((f) => ({ ...f, industry: v }))} />
      </Section>

      <Section 
        title="Company Logo" 
        description="Shown on payslips and wherever the company logo appears. PNG or JPG, max 2MB."
      >
        <AssetUploadSection
          title="Company Logo"
          description="Shown on payslips and wherever the company logo appears."
          currentUrl={form.logoUrl}
          onUpload={uploadLogo}
          onRemove={removeLogo}
          uploading={uploadingLogo}
          removing={removingLogo}
          uploadLabel="Upload Logo"
          removeLabel="Remove Logo"
        />
      </Section>

      <Section 
        title="Authorised Signatory" 
        description="Upload signature image for quotation, proforma invoice, and tax invoice. PNG or JPG, max 2MB."
      >
        <AssetUploadSection
          title="Authorised Signatory"
          description="Upload signature image for quotation, proforma invoice, and tax invoice."
          currentUrl={form.signatureUrl}
          onUpload={uploadSignature}
          onRemove={removeSignature}
          uploading={uploadingSignature}
          removing={removingSignature}
          uploadLabel="Upload Signature"
          removeLabel="Remove Signature"
        />
      </Section>

      {/* Finance Master Data */}
      <Section
        title="Finance Master Data"
        description="Legacy bank details field, kept for reference only."
      >
        {/* Informational Banner */}
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200">Not used on documents</h4>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                Quotations, Proforma Invoices, and Tax Invoices now pull bank details from the dedicated Bank Details admin page (the record marked Default). This section no longer feeds into any document and is kept only for reference.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-foreground">Bank Account Details</h4>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="bank-acc-name" className="text-xs font-semibold">Account Name <span className="text-red-500">*</span></Label>
              <Input 
                id="bank-acc-name" 
                value={form.bankAccName} 
                onChange={(e) => setForm((f) => ({ ...f, bankAccName: e.target.value }))} 
                placeholder="DURRANI TECH PRIVATE LIMITED"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bank-acc-no" className="text-xs font-semibold">Account Number <span className="text-red-500">*</span></Label>
              <Input 
                id="bank-acc-no" 
                value={form.bankAccNo} 
                onChange={(e) => setForm((f) => ({ ...f, bankAccNo: e.target.value }))} 
                placeholder="258888148121"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bank-name" className="text-xs font-semibold">Bank Name <span className="text-red-500">*</span></Label>
              <Input 
                id="bank-name" 
                value={form.bankName} 
                onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))} 
                placeholder="IndusInd Bank"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bank-branch" className="text-xs font-semibold">Branch</Label>
              <Input 
                id="bank-branch" 
                value={form.bankBranch} 
                onChange={(e) => setForm((f) => ({ ...f, bankBranch: e.target.value }))} 
                placeholder="Camp, Pune"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bank-ifsc" className="text-xs font-semibold">IFSC Code <span className="text-red-500">*</span></Label>
              <Input 
                id="bank-ifsc" 
                value={form.bankIfsc} 
                onChange={(e) => setForm((f) => ({ ...f, bankIfsc: e.target.value }))} 
                placeholder="INDB0001812"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bank-upi" className="text-xs font-semibold">UPI ID</Label>
              <Input 
                id="bank-upi" 
                value={form.bankUpi} 
                onChange={(e) => setForm((f) => ({ ...f, bankUpi: e.target.value }))} 
                placeholder="durranitech@indus"
              />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Registration & Tax">
        <Field label="GSTIN" id="cp-gstin" value={form.gstin} onChange={(v) => setForm((f) => ({ ...f, gstin: v }))} />
        <Field label="PAN" id="cp-pan" value={form.pan} onChange={(v) => setForm((f) => ({ ...f, pan: v }))} />
        <Field label="CIN" id="cp-cin" value={form.cin} onChange={(v) => setForm((f) => ({ ...f, cin: v }))} />
      </Section>

      <Section title="Registered Address">
        <Field label="Address Line 1" id="cp-addr1" value={form.registeredAddress.line1} onChange={(v) => setAddress('line1', v)} />
        <Field label="Address Line 2" id="cp-addr2" value={form.registeredAddress.line2} onChange={(v) => setAddress('line2', v)} />
        <Field label="City" id="cp-city" value={form.registeredAddress.city} onChange={(v) => setAddress('city', v)} />
        <Field label="State" id="cp-state" value={form.registeredAddress.state} onChange={(v) => setAddress('state', v)} />
        <Field label="Country" id="cp-country" value={form.registeredAddress.country} onChange={(v) => setAddress('country', v)} />
        <Field label="Pincode" id="cp-pincode" value={form.registeredAddress.pincode} onChange={(v) => setAddress('pincode', v)} />
      </Section>

      <Section title="Contact">
        <Field label="Official Email" id="cp-email" type="email" value={form.officialEmail} onChange={(v) => setForm((f) => ({ ...f, officialEmail: v }))} />
        <Field label="Phone" id="cp-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
        <Field label="Website" id="cp-website" value={form.website} onChange={(v) => setForm((f) => ({ ...f, website: v }))} />
      </Section>

      <Section title="Regional">
        <Field label="Timezone" id="cp-timezone" value={form.timezone} onChange={(v) => setForm((f) => ({ ...f, timezone: v }))} />
        <Field label="Default Currency" id="cp-currency" value={form.defaultCurrency} onChange={(v) => setForm((f) => ({ ...f, defaultCurrency: v }))} />
        <Field
          label="Fiscal Year Start Month (1-12)"
          id="cp-fy"
          type="number"
          value={form.fiscalYearStartMonth}
          onChange={(v) => setForm((f) => ({ ...f, fiscalYearStartMonth: Math.min(12, Math.max(1, Number(v) || 1)) }))}
        />
      </Section>

      <Section title="Working Hours">
        <Field 
          label="Start Time" 
          id="cp-wh-start" 
          type="time" 
          value={form.workingHours.start} 
          onChange={(v) => setForm((f) => ({ ...f, workingHours: { ...f.workingHours, start: v } }))} 
        />
        <Field 
          label="End Time" 
          id="cp-wh-end" 
          type="time" 
          value={form.workingHours.end} 
          onChange={(v) => setForm((f) => ({ ...f, workingHours: { ...f.workingHours, end: v } }))} 
        />
      </Section>

      <Section title="Work Week (Timesheets)">
        <div className="sm:col-span-2 space-y-2">
          <p className="text-xs text-muted-foreground">
            The days people normally work, and the hours in a working day. Timesheets compare logged time with this,
            after holidays and approved leave.
          </p>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Working days">
            {[1, 2, 3, 4, 5, 6, 0].map((d) => {
              const on = form.workWeek.workDays.includes(d)
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setForm((f) => {
                      const days = f.workWeek.workDays.includes(d) ? f.workWeek.workDays.filter((x) => x !== d) : [...f.workWeek.workDays, d]
                      return days.length === 0 ? f : { ...f, workWeek: { ...f.workWeek, workDays: days.sort((a, b) => a - b) } }
                    })
                  }
                  className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'}`}
                >
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]}
                </button>
              )
            })}
          </div>
        </div>
        <Field
          label="Hours in a working day"
          id="cp-ww-hours"
          type="number"
          value={form.workWeek.hoursPerDay}
          onChange={(v) => setForm((f) => ({ ...f, workWeek: { ...f.workWeek, hoursPerDay: Math.min(24, Math.max(1, Number(v) || 8)) } }))}
        />
      </Section>

      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  )
}
